// frontend/src/components/Search/AutocompleteDropdown.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { AutocompleteResult } from '../../services/api';
import { useLanguage } from '../../hooks/useLanguage';
import { Stethoscope, Building2, Briefcase, User, ChevronRight, Video } from 'lucide-react';

interface AutocompleteDropdownProps {
  results: AutocompleteResult[];
  isLoading: boolean;
  onSelect: () => void;
}

const AutocompleteDropdown: React.FC<AutocompleteDropdownProps> = ({
  results,
  isLoading,
  onSelect,
}) => {
  const { t } = useLanguage();
  if (isLoading) {
    return (
      <div className="absolute z-50 w-full mt-2 bg-white dark:bg-dark-card rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return null;
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'symptom':
        return <Stethoscope className="w-5 h-5 text-red-500" />;
      case 'department':
        return <Building2 className="w-5 h-5 text-blue-500" />;
      case 'service':
        return <Briefcase className="w-5 h-5 text-green-500" />;
      case 'doctor':
        return <User className="w-5 h-5 text-purple-500" />;
      default:
        return null;
    }
  };

  const getLink = (result: AutocompleteResult) => {
    switch (result.type) {
      case 'symptom':
        // For symptoms with exactly ONE department, link directly to it
        if (result.extra?.departments && result.extra.departments.length === 1) {
          return `/departments/${result.extra.departments[0].slug}`;
        }
        // For symptoms with MULTIPLE departments, show search page
        if (result.extra?.departments && result.extra.departments.length > 1) {
          return `/search?q=${encodeURIComponent(result.name)}`;
        }
        return '#';
      case 'department':
        return `/departments/${result.slug}`;
      case 'service':
        return `/services/${result.slug}`;
      case 'doctor':
        return `/doctors/${result.slug}`;
      default:
        return '#';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'symptom':
        return t('search.types.symptom');
      case 'department':
        return t('search.types.department');
      case 'service':
        return t('search.types.service');
      case 'doctor':
        return t('search.types.doctor');
      default:
        return '';
    }
  };

  const formatPrice = (price: number | null | undefined) => {
    if (!price) return null;
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'AMD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="absolute z-50 w-full mt-2 bg-white dark:bg-dark-card rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden max-h-96 overflow-y-auto">
      {results.map((result, index) => {
        const link = getLink(result);
        const isClickable = link !== '#';

        return (
          <Link
            key={`${result.type}-${result.id}`}
            to={link}
            onClick={(e) => {
              if (!isClickable) {
                e.preventDefault();
              } else {
                onSelect();
              }
            }}
            className={`
              flex items-center justify-between px-4 py-3
              ${index !== 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}
              ${isClickable ? 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer' : 'cursor-default'}
              transition-colors duration-150
            `}
          >
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {getIcon(result.type)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {result.name}
                  </p>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {getTypeLabel(result.type)}
                  </span>
                </div>

                {/* Extra information based on type */}
                {result.type === 'symptom' && result.extra?.departments && result.extra.departments.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {result.extra.departments.map(d => d.name).join(', ')}
                  </p>
                )}

                {result.type === 'service' && (
                  <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                    {result.extra?.is_online && (
                      <span className="inline-flex items-center gap-1 font-medium text-[#0c63e9] dark:text-[#599bff] flex-shrink-0">
                        <Video className="w-3 h-3" aria-hidden />
                        {t('online.badge')}
                      </span>
                    )}
                    {result.extra?.department && (
                      <span className="truncate">{result.extra.department}</span>
                    )}
                    {result.extra?.price && (
                      <span className="font-medium text-green-600 dark:text-green-400 flex-shrink-0">
                        {formatPrice(result.extra.price)}
                      </span>
                    )}
                  </div>
                )}

                {result.type === 'doctor' && result.extra?.specialization && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {result.extra.specialization}
                  </p>
                )}
              </div>
            </div>

            {isClickable && (
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
            )}
          </Link>
        );
      })}
    </div>
  );
};

export default AutocompleteDropdown;
